import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { ProgressTooltip } from "./memorization-progress-tooltip";


interface IChartPoint{
  date: string;
  planned: number;
  actualAyatPct: number | null;
  forecastAyatPct: number | null;
};

export type MemorizationProgressGrafProps = {
    chartData: IChartPoint[];
}

export function MemorizationProgressGraf( props: MemorizationProgressGrafProps) {

   const {chartData} = props;
   const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 576 : false
   );
  const [openInfoKey, setOpenInfoKey] = useState<"planned" | "actual" | "forecast" | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const formatXAxisTick = (value: string) => {
    if (!value) return "";
    if (!isMobile) return value;

    const parts = value.split(" ");
    // Mobile: keep year, but compress it to 2 digits for better readability.
    if (parts.length >= 3) {
      const shortYear = parts[2].slice(-2);
      return `${parts[0]} ${parts[1]} ${shortYear}`;
    }

    return value;
  };

  if (!chartData || !chartData.length) {
    return <div>Ingen færdige memoreringer fundet.</div>;
  }

  return (
    <div style={{ width: "100%", maxHeight: "300px", overflowY: "auto" }}>
      
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: isMobile ? 35 : 10 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            minTickGap={isMobile ? 8 : 30}
            tickFormatter={formatXAxisTick}
            angle={isMobile ? -35 : 0}
            textAnchor={isMobile ? "end" : "middle"}
            height={isMobile ? 55 : 30}
            tickMargin={isMobile ? 10 : 4}
            interval="preserveStartEnd"
            tick={{ fontSize: isMobile ? 10 : 12 }}
          />
          <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
          <Tooltip content={<ProgressTooltip />} />

          <Line
            type="monotone"
            dataKey="planned"
            name="Planlagt fremdrift"
            stroke="#2563eb"
            strokeWidth={2}
            dot={false}
          />

          <Line
            type="monotone"
            dataKey="actualAyatPct"
            name="Faktisk fremdrift"
            stroke="#f59e0b"
            strokeWidth={3}
            connectNulls
          />

          <Line
            type="monotone"
            dataKey="forecastAyatPct"
            name="Forventet fremdrift"
            stroke="#16a34a"
            strokeWidth={2}
            strokeDasharray="6 6"
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>

      <div style={{ marginTop: "10px", paddingLeft: "4px" }}>
        <div style={{ marginBottom: "8px", fontSize: "14px", lineHeight: 1.4 }}>
          {openInfoKey === "planned" ? (
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #dbe4ee",
                borderRadius: "8px",
                padding: "8px 10px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "4px" }}>
                <button
                  type="button"
                  onClick={() => setOpenInfoKey(null)}
                  style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "14px", color: "#475569" }}
                  aria-label="Luk forklaring"
                >
                  x
                </button>
              </div>
              <div style={{ fontSize: "12px", color: "#334155", marginBottom: "4px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#0f766e", marginBottom: "2px" }}>
                  English
                </div>
                <div>
                  Planned progress: Shows where the student should ideally be at this point to finish on schedule based on the selected study program.
                </div>
              </div>
              <div style={{ fontSize: "12px", color: "#334155", direction: "rtl", textAlign: "right" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#0f766e", marginBottom: "2px" }}>
                  العربية
                </div>
                <div>
                  التقدم المخطط: يوضح أين يفترض أن يكون الطالب في هذه المرحلة ليُنهي الحفظ في الموعد حسب البرنامج الدراسي المختار.
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <button
                type="button"
                onMouseEnter={() => setOpenInfoKey("planned")}
                onFocus={() => setOpenInfoKey("planned")}
                onClick={() => setOpenInfoKey("planned")}
                style={{
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  border: "none",
                  backgroundColor: "#2563eb",
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: 700,
                  lineHeight: "18px",
                  textAlign: "center",
                  marginTop: "2px",
                  marginRight: "8px",
                  flexShrink: 0,
                  cursor: "pointer",
                }}
                aria-label="Vis forklaring for planlagt fremdrift"
              >
                i
              </button>
              <span>
                <strong>Planlagt fremdrift:</strong> Viser hvor eleven ideelt burde vaere pa dette tidspunkt i forloebet for at blive faerdig til planlagt tid i forhold til elevens valgte studie program.
              </span>
            </div>
          )}
        </div>

        <div style={{ marginBottom: "8px", fontSize: "14px", lineHeight: 1.4 }}>
          {openInfoKey === "actual" ? (
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #dbe4ee",
                borderRadius: "8px",
                padding: "8px 10px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "4px" }}>
                <button
                  type="button"
                  onClick={() => setOpenInfoKey(null)}
                  style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "14px", color: "#475569" }}
                  aria-label="Luk forklaring"
                >
                  x
                </button>
              </div>
              <div style={{ fontSize: "12px", color: "#334155", marginBottom: "4px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#0f766e", marginBottom: "2px" }}>
                  English
                </div>
                <div>
                  Actual progress: Shows the student's real progress based on memorization entries that have already been completed.
                </div>
              </div>
              <div style={{ fontSize: "12px", color: "#334155", direction: "rtl", textAlign: "right" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#0f766e", marginBottom: "2px" }}>
                  العربية
                </div>
                <div>
                  التقدم الفعلي: يوضح التقدم الحقيقي للطالب بناءً على سجلات الحفظ التي تم إكمالها بالفعل.
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <button
                type="button"
                onMouseEnter={() => setOpenInfoKey("actual")}
                onFocus={() => setOpenInfoKey("actual")}
                onClick={() => setOpenInfoKey("actual")}
                style={{
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  border: "none",
                  backgroundColor: "#f59e0b",
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: 700,
                  lineHeight: "18px",
                  textAlign: "center",
                  marginTop: "2px",
                  marginRight: "8px",
                  flexShrink: 0,
                  cursor: "pointer",
                }}
                aria-label="Vis forklaring for faktisk fremdrift"
              >
                i
              </button>
              <span>
                <strong>Faktisk fremdrift:</strong> Viser elevens reelle udvikling baseret pa de memoreringer, der allerede er registreret som gennemfoert.
              </span>
            </div>
          )}
        </div>

        <div style={{ fontSize: "14px", lineHeight: 1.4 }}>
          {openInfoKey === "forecast" ? (
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #dbe4ee",
                borderRadius: "8px",
                padding: "8px 10px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "4px" }}>
                <button
                  type="button"
                  onClick={() => setOpenInfoKey(null)}
                  style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "14px", color: "#475569" }}
                  aria-label="Luk forklaring"
                >
                  x
                </button>
              </div>
              <div style={{ fontSize: "12px", color: "#334155", marginBottom: "4px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#0f766e", marginBottom: "2px" }}>
                  English
                </div>
                <div>
                  Forecast progress: Estimates future progress if the student continues at the current pace.
                </div>
              </div>
              <div style={{ fontSize: "12px", color: "#334155", direction: "rtl", textAlign: "right" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#0f766e", marginBottom: "2px" }}>
                  العربية
                </div>
                <div>
                  التقدم المتوقع: يقدّر التقدم المستقبلي إذا استمر الطالب بنفس الوتيرة الحالية.
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <button
                type="button"
                onMouseEnter={() => setOpenInfoKey("forecast")}
                onFocus={() => setOpenInfoKey("forecast")}
                onClick={() => setOpenInfoKey("forecast")}
                style={{
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  border: "none",
                  backgroundColor: "#16a34a",
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: 700,
                  lineHeight: "18px",
                  textAlign: "center",
                  marginTop: "2px",
                  marginRight: "8px",
                  flexShrink: 0,
                  cursor: "pointer",
                }}
                aria-label="Vis forklaring for forventet fremdrift"
              >
                i
              </button>
              <span>
                <strong>Forventet fremdrift:</strong> Estimat af fremtidig udvikling, hvis eleven fortsaetter i samme tempo som nu.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}